/**
 * Column type detection utilities for chart configuration.
 * Detects whether columns contain numeric, date, or string values.
 */

import type { ColumnDataCategory, ColumnTypeInfo } from '../types/chartTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
// BUNDLE ISOLATION (spatial support Task 27): the ONLY spatial-decode import a chart may take is
// the EAGER header sniffer. Never import decodeSpatialValue / wkx / the lazy chunk here — charts
// must not pull the spatial decode chunk (see scripts/assert-spatial-lazy-chunks.mjs).
import { sniffSpatialValue } from '../../spatial/decode/spatialSniff';

/** SQL data types that are numeric */
const NUMERIC_DATA_TYPES = new Set([
  'int',
  'integer',
  'bigint',
  'smallint',
  'tinyint',
  'decimal',
  'numeric',
  'float',
  'real',
  'double',
  'double precision',
  'money',
  'smallmoney',
  'number',
]);

/** SQL data types that are dates/times */
const DATE_DATA_TYPES = new Set([
  'date',
  'datetime',
  'datetime2',
  'datetimeoffset',
  'smalldatetime',
  'time',
  'timestamp',
  'timestamptz',
  'timestamp with time zone',
  'timestamp without time zone',
]);

/** SQL data types that are booleans */
const BOOLEAN_DATA_TYPES = new Set(['bit', 'boolean', 'bool']);

/**
 * SQL data types that are spatial/geometry across all 5 engines' native type
 * names (Task 27 — spec §12: geometry is neither categorical nor numeric).
 * Base names mirror `spatialColumnCategorization.NATIVE_SPATIAL_TYPES`,
 * duplicated here rather than imported to keep this file's only spatial
 * dependency the EAGER header sniffer (bundle isolation).
 */
const SPATIAL_DATA_TYPES = new Set([
  'geometry',
  'geography',
  'point',
  'linestring',
  'polygon',
  'multipoint',
  'multilinestring',
  'multipolygon',
  'geometrycollection',
  'sdo_geometry',
]);

// WKT-looking text (mirrors `spatialDeclarableSuggest.WKT_LOOKING_RE`, duplicated for the same
// bundle-isolation reason): an optional `SRID=n;` prefix followed by one of the 7 WKT geometry
// keywords and an opening paren.
const WKT_LOOKING_RE =
  /^\s*(?:SRID=\d+;)?\s*(?:POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)\s*\(/iu;

/**
 * Checks if a value looks spatial: WKT text, or EWKB/WKB hex via the eager
 * header sniffer (canonical pgsql/mock EWKB dialect — engine-agnostic enough
 * for a coarse chart-axis exclusion). Belt-and-suspenders fallback for
 * columns whose declared data type didn't already flag them and that have no
 * precomputed `spatialColumnIds` (e.g. QE expression columns with no
 * source-table provenance for MetaInfo-declared detection). Never decodes —
 * header/pattern checks only.
 */
export function isSpatialValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && WKT_LOOKING_RE.test(value)) return true;
  return sniffSpatialValue('pgsql', value) !== null;
}

/**
 * Checks if a value is numeric (number or numeric string)
 */
export function isNumericValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return !isNaN(value) && isFinite(value);
  if (typeof value === 'bigint') return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return false;
    const parsed = Number(trimmed);
    return !isNaN(parsed) && isFinite(parsed);
  }
  return false;
}

/**
 * Checks if a value is a date (Date object or ISO date string)
 */
export function isDateValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return false;
    // Check for common date patterns
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}/;
    if (!isoDatePattern.test(trimmed)) return false;
    const parsed = Date.parse(trimmed);
    return !isNaN(parsed);
  }
  return false;
}

/**
 * Checks if a value is a boolean
 */
export function isBooleanValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return value === 0 || value === 1;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return ['true', 'false', '0', '1', 'yes', 'no'].includes(lower);
  }
  return false;
}

/**
 * Determines the data category from a SQL data type string
 */
export function getCategoryFromDataType(dataType?: string): ColumnDataCategory {
  if (!dataType) return 'unknown';

  const normalized = dataType.toLowerCase().trim();

  // Remove precision/scale info (e.g., "decimal(10,2)" -> "decimal")
  const baseType = normalized.replace(/\(.*\)/, '').trim();

  if (SPATIAL_DATA_TYPES.has(baseType)) return 'unsupported';
  if (NUMERIC_DATA_TYPES.has(baseType)) return 'numeric';
  if (DATE_DATA_TYPES.has(baseType)) return 'date';
  if (BOOLEAN_DATA_TYPES.has(baseType)) return 'boolean';

  // Check for varchar, char, text, etc.
  if (
    baseType.includes('char') ||
    baseType.includes('text') ||
    baseType.includes('string') ||
    baseType === 'nvarchar' ||
    baseType === 'nchar' ||
    baseType === 'ntext'
  ) {
    return 'string';
  }

  return 'unknown';
}

/**
 * Detects the data category by sampling values from a column
 */
export function detectCategoryFromValues(
  rows: Record<string, unknown>[],
  columnName: string,
  maxSamples = 50
): { category: ColumnDataCategory; confidence: number } {
  const sampleSize = Math.min(rows.length, maxSamples);
  if (sampleSize === 0) return { category: 'unknown', confidence: 0 };

  let numericCount = 0;
  let dateCount = 0;
  let booleanCount = 0;
  let stringCount = 0;
  let spatialCount = 0;
  let nonNullCount = 0;

  for (let i = 0; i < sampleSize; i++) {
    const value = rows[i]?.[columnName];
    if (value === null || value === undefined) continue;

    nonNullCount++;

    // Spatial check runs first — EWKB hex / WKT text would otherwise fall
    // into the generic string bucket below (Task 27 value-sniff fallback).
    if (isSpatialValue(value)) spatialCount++;
    else if (isNumericValue(value)) numericCount++;
    else if (isDateValue(value)) dateCount++;
    else if (isBooleanValue(value)) booleanCount++;
    else if (typeof value === 'string') stringCount++;
  }

  if (nonNullCount === 0) return { category: 'unknown', confidence: 0 };

  // Determine category based on highest match count
  const counts = [
    { category: 'numeric' as const, count: numericCount },
    { category: 'date' as const, count: dateCount },
    { category: 'boolean' as const, count: booleanCount },
    { category: 'string' as const, count: stringCount },
    { category: 'unsupported' as const, count: spatialCount },
  ];

  const best = counts.reduce((a, b) => (b.count > a.count ? b : a));
  const confidence = best.count / nonNullCount;

  // If no clear winner, default to string
  if (best.count === 0) return { category: 'string', confidence: 0 };

  return { category: best.category, confidence };
}

/**
 * Analyzes columns and returns type information for each.
 *
 * `spatialColumnIds` (Task 27, A19/G18) — an OPTIONAL precomputed set of
 * spatial column ids (native + declared, keyed by `col.id ?? col.name` —
 * mirrors `resultColumnId` in `spatial/results-map/spatialResultColumns.ts`).
 * Callers that already ran the grid's spatial categorization (which also
 * catches WKT/GeoJSON-declared columns the value-sniff fallback below can't
 * see) should pass it in rather than relying solely on re-sniffing here.
 */
export function analyzeColumns(
  columns: QueryColumn[],
  rows: Record<string, unknown>[],
  spatialColumnIds?: ReadonlySet<string>
): ColumnTypeInfo[] {
  return columns.map((col, index) => {
    // Rows are keyed by the deduplicated column id (`col_<i>` for a duplicate or
    // blank name); `name` stays the label the grid shows for the same column.
    const key = col.id ?? col.name;
    const name = col.name || `Column ${index + 1}`;
    if (spatialColumnIds?.has(key)) {
      return {
        key,
        name,
        dataType: col.dataType,
        category: 'unsupported',
        confidence: 1.0,
      };
    }

    // First try to get category from declared data type
    const typeCategory = getCategoryFromDataType(col.dataType);

    if (typeCategory !== 'unknown') {
      return {
        key,
        name,
        dataType: col.dataType,
        category: typeCategory,
        confidence: 1.0,
      };
    }

    // Fall back to value detection
    const { category, confidence } = detectCategoryFromValues(rows, key);

    return {
      key,
      name,
      dataType: col.dataType,
      category,
      confidence,
    };
  });
}

/**
 * Filters columns by data category
 */
export function getColumnsByCategory(
  columnInfos: ColumnTypeInfo[],
  category: ColumnDataCategory
): ColumnTypeInfo[] {
  return columnInfos.filter((col) => col.category === category);
}

/**
 * Gets columns suitable for X-axis (categories, dates, or numeric for scatter)
 */
export function getXAxisCandidates(columnInfos: ColumnTypeInfo[]): ColumnTypeInfo[] {
  return columnInfos.filter(
    (col) =>
      col.category === 'string' ||
      col.category === 'date' ||
      col.category === 'numeric'
  );
}

/**
 * Gets columns suitable for Y-axis (numeric values)
 */
export function getYAxisCandidates(columnInfos: ColumnTypeInfo[]): ColumnTypeInfo[] {
  return columnInfos.filter((col) => col.category === 'numeric');
}

/**
 * Gets columns suitable for series grouping (categorical)
 */
export function getSeriesCandidates(columnInfos: ColumnTypeInfo[]): ColumnTypeInfo[] {
  return columnInfos.filter(
    (col) => col.category === 'string' || col.category === 'boolean'
  );
}
